import Alert from "components/common/Alert";
import { useEffect, useMemo, useRef, useState } from "react";
import { getExhibitorDropdown } from "api/exhibitor.api";
import { getImageUrl } from "utils/imageHelper";
import {
  InputField,
  SelectField,
  ImageUploadField,
  TextareaField,
} from ".././FormFields";
import PageLayout from ".././PageFromLayout";
import FormSection from ".././FormSection";
import { validateImageFile } from "utils/common";
export default function ArticleForm({
  initialData = null,
  onSubmit,
  onBack,
  submitLabel,
}) {
  const alertRef = useRef(null);
  const fileRef = useRef(null);

  const [alert, setAlert] = useState("");
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [exhibitors, setExhibitors] = useState([]);

  const normalizePath = (p) =>
    typeof p === "string" ? p.replace(/\\/g, "/") : "";

  const formatInputDateTimeLocal = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
      d.getDate()
    )}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const [formData, setFormData] = useState({
    exhibitor_id: "",
    title: "",
    author: "",
    description: "",
    published_date: "",
    status: "active",
    articleImages: [],
    articleVideos: [],
    existingImages: [],
    existingVideos: [],
    removed_media: [],
    default_media: null,
  });

  useEffect(() => {
    if (!initialData?._id) return;

    setFormData({
      exhibitor_id: initialData.exhibitor_id?._id || "",
      title: initialData.title || "",
      author: initialData.author || "",
      description: initialData.description || "",
      published_date: formatInputDateTimeLocal(initialData.published_date),
      status: initialData.status || "active",
      articleImages: [],
      articleVideos: [],
      existingImages: (initialData.articleImages || []).map(normalizePath),
      existingVideos: (initialData.articleVideos || []).map(normalizePath),
      removed_media: [],
      default_media: initialData.default_media
        ? JSON.stringify({
            ...initialData.default_media,
            value: normalizePath(initialData.default_media.value),
          })
        : null,
    });
  }, [initialData]);

  useEffect(() => {
    const companyId = localStorage.getItem("company_id");
    if (!companyId) return;

    getExhibitorDropdown(companyId)
      .then((res) => {
        const exhibitors = (res.data || []).map((item) => ({
          value: item._id,
          label: item.title,
        }));
        setExhibitors(exhibitors);
      })
      .catch(() => setExhibitors([]));
  }, []);

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    // 1. Separate images and videos
    const images = files.filter((f) => f.type.startsWith("image/"));
    const videos = files.filter((f) => f.type.startsWith("video/"));

    // 2. Validate ONLY images (e.g. max 5 MB per image)
    const imageValidationResults = await Promise.all(
      images.map((file) => validateImageFile(file, 3))
    );

    // 3. Check for any image validation errors
    const invalidImage = imageValidationResults.find((res) => !res.isValid);
    if (invalidImage) {
      setErrors((prev) => ({
        ...prev,
        existingImages: invalidImage.error,
      }));
      return; // Block update if an image fails validation
    }

    // 4. Clear previous error state
    setErrors((prev) => ({
      ...prev,
      existingImages: "",
    }));

    // 5. Update state for both images and videos
    setFormData((prev) => ({
      ...prev,
      articleImages: [...(prev.articleImages || []), ...images],
      articleVideos: [...(prev.articleVideos || []), ...videos],
    }));
  };

  const removeNewMedia = (index, type) => {
    setFormData((prev) => ({
      ...prev,
      [type]:
        type === "articleImages"
          ? prev.articleImages.filter((_, i) => i !== index)
          : prev.articleVideos.filter((_, i) => i !== index),
    }));
  };

  const removeExistingMedia = (index, type) => {
    setFormData((prev) => ({
      ...prev,
      [type]:
        type === "existingImages"
          ? prev.existingImages.filter((_, i) => i !== index)
          : prev.existingVideos.filter((_, i) => i !== index),
      removed_media: [...prev.removed_media, prev[type][index]],
    }));
  };

  const defaultMeta = useMemo(() => {
    if (!formData.default_media) return null;
    if (typeof formData.default_media === "object")
      return formData.default_media;
    try {
      return JSON.parse(formData.default_media);
    } catch {
      return null;
    }
  }, [formData.default_media]);

  const setDefaultMedia = (config) => {
    setFormData((prev) => ({
      ...prev,
      default_media: JSON.stringify(config),
    }));
  };

  const isDefault = (config) => {
    if (!defaultMeta) return false;

    if (defaultMeta.type !== config.type) return false;
    if (defaultMeta.mediaType !== config.mediaType) return false;

    if (config.type === "existing") {
      return normalizePath(defaultMeta.value) === normalizePath(config.value);
    }

    return defaultMeta.value === config.value;
  };

  const validate = () => {
    const e = {};
    if (!formData.exhibitor_id) e.exhibitor_id = "Exhibitor is required";
    if (!formData.title.trim()) e.title = "Title is required";
    if (!formData.author.trim()) e.author = "Author is required";
    if (!formData.published_date)
      e.published_date = "Published date is required";
    return e;
  };

  const submit = async (e) => {
    e.preventDefault();

    const v = validate();
    if (Object.keys(v).length) {
      setErrors(v);
      setAlert("Please fix the errors below");
      return;
    }

    const fd = new FormData();

    [
      "exhibitor_id",
      "title",
      "author",
      "description",
      "published_date",
      "status",
    ].forEach((k) => fd.append(k, formData[k]));

    formData.articleImages.forEach((file) => fd.append("articleImages", file));
    formData.articleVideos.forEach((file) => fd.append("articleVideos", file));

    if (formData.default_media) {
      fd.append("default_media", formData.default_media);
    }

    if (formData.removed_media.length) {
      fd.append("removed_media", JSON.stringify(formData.removed_media));
    }

    setSubmitting(true);
    try {
      await onSubmit(fd);
    } finally {
      setSubmitting(false);
    }
  };
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setErrors((p) => ({
      ...p,
      [name]: "",
    }));
  };

  return (
    <PageLayout
      onBack={onBack}
      onSubmit={submit}
      submitText={initialData ? "Update Article" : "Create Article"}
      loading={submitting}
    >
      <form onSubmit={submit} className="flex flex-col gap-6">
        {alert && (
          <div ref={alertRef}>
            <Alert type="error" message={alert} onClose={() => setAlert("")} />
          </div>
        )}
        <FormSection title="Article Details">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <SelectField
              label="Exhibitor"
              name="exhibitor_id"
              value={formData.exhibitor_id || ""}
              onChange={handleChange}
              options={exhibitors}
              placeholder="Search exhibitor..."
              isSearchable={true}
              required
              error={errors.exhibitor_id}
            />

            <InputField
              label="Title"
              name="title"
              value={formData.title || ""}
              onChange={handleChange}
              placeholder="Enter title"
              error={errors.title}
              required
            />
            <InputField
              label="Author"
              name="author"
              value={formData.author || ""}
              onChange={handleChange}
              placeholder="Enter author"
              error={errors.author}
              required
            />
            <InputField
              type="datetime-local"
              label="Published Date"
              name="published_date"
              value={formData.published_date || ""}
              onChange={handleChange}
              placeholder="Enter author"
              error={errors.published_date}
              required
            />
            <TextareaField
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Enter description ..."
              rows={3}
              error={errors.description}
            />
            <SelectField
              label="Status"
              name="status"
              value={formData.status}
              onChange={handleChange}
              options={[
                { label: "Active", value: "active" },
                { label: "Inactive", value: "inactive" },
              ]}
              placeholder="Search status..."
              error={errors.status}
            />
          </div>
        </FormSection>
        {/* <Section title="Article Details"> */}
        {/* <SelectInput
          label="Exhibitor"
          required
          value={formData.exhibitor_id}
          error={errors.exhibitor_id}
          onChange={(e) =>
            setFormData((p) => ({ ...p, exhibitor_id: e.target.value }))
          }
        >
          <option value="">Select Exhibitor</option>
          {exhibitors.map((e) => (
            <option key={e._id} value={e._id}>
              {e.title}
            </option>
          ))}

          {/* <Input
          label="Title"
          required
          value={formData.title}
          error={errors.title}
          onChange={(e) =>
            setFormData((p) => ({ ...p, title: e.target.value }))
          }
        /> */}

        {/* <Input
          label="Author"
          required
          value={formData.author}
          error={errors.author}
          onChange={(e) =>
            setFormData((p) => ({ ...p, author: e.target.value }))
          }
        /> */}

        {/* <Input
          type="datetime-local"
          label="Published Date"
          required
          value={formData.published_date}
          error={errors.published_date}
          onChange={(e) =>
            setFormData((p) => ({ ...p, published_date: e.target.value }))
          }
        /> */}

        {/* <Textarea
            label="Description"
            value={formData.description}
            onChange={(e) =>
              setFormData((p) => ({ ...p, description: e.target.value }))
            }
          /> */}

        {/* <SelectInput
            label="Status"
            value={formData.status}
            onChange={(e) =>
              setFormData((p) => ({ ...p, status: e.target.value }))
            }
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </SelectInput> */}
        {/* </Section> */}

        <FormSection title="Article Media">
          <div className="md:col-span-2">
            <ImageUploadField
              // label="Gallery Images"
              multiple={true}
              // preview={formData.productImages||[]} // Array of URLs: ["blob:http...", "blob:http..."]
              onUpload={handleFileChange}
              // onRemove={(index) => handleRemoveGalleryImage(index)}
              showRemove={true}
              name="existingImages"
              accept="image/*,video/*"
              error={errors.existingImages}
            />
            {/* <input
              type="file"
              ref={fileRef}
              multiple
              accept="image/*,video/*"
              onChange={handleFileChange}
              className="w-full rounded-xl border p-3"
            /> */}

            <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-4">
              {formData.existingImages?.map((img, index) => {
                const config = {
                  type: "existing",
                  mediaType: "image",
                  value: img,
                };
                const highlighted = isDefault(config);

                return (
                  <div
                    key={`ex-img-${index}`}
                    className={`relative rounded-xl border p-1 transition ${
                      highlighted
                        ? "scale-[1.02] shadow-lg ring-2 ring-brand-500"
                        : "hover:shadow-md"
                    }`}
                  >
                    <img
                      src={getImageUrl(img)}
                      alt=""
                      className="h-32 w-full rounded-lg object-cover"
                    />
                    <div className="mt-1 flex justify-between text-xs">
                      {highlighted ? (
                        <span className="text-xs font-medium text-green-600">
                          Default Image
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setDefaultMedia(config)}
                          className="text-blue-600"
                        >
                          Set Default
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() =>
                          removeExistingMedia(index, "existingImages")
                        }
                        className="text-red-600"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                );
              })}

              {formData.articleImages?.map((img, index) => {
                const config = {
                  type: "new",
                  mediaType: "image",
                  value: index,
                };
                const highlighted = isDefault(config);

                return (
                  <div
                    key={`new-img-${index}`}
                    className={`relative rounded-xl border p-1 transition ${
                      highlighted
                        ? "scale-[1.02] shadow-lg ring-2 ring-brand-500"
                        : "hover:shadow-md"
                    }`}
                  >
                    <img
                      src={URL.createObjectURL(img)}
                      alt=""
                      className="h-32 w-full rounded-lg object-cover"
                    />
                    <div className="mt-1 flex justify-between text-xs">
                      {highlighted ? (
                        <span className="text-xs font-medium text-green-600">
                          Default Image
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setDefaultMedia(config)}
                          className="text-blue-600"
                        >
                          Set Default
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => removeNewMedia(index, "articleImages")}
                        className="text-red-600"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                );
              })}

              {formData.articleVideos?.map((vid, index) => {
                const config = {
                  type: "new",
                  mediaType: "video",
                  value: index,
                };
                const highlighted = isDefault(config);

                return (
                  <div
                    key={`new-vid-${index}`}
                    className={`relative rounded-xl border p-1 transition ${
                      highlighted
                        ? "scale-[1.02] shadow-lg ring-2 ring-brand-500"
                        : "hover:shadow-md"
                    }`}
                  >
                    <video
                      className="h-32 w-full rounded-lg object-cover"
                      controls
                    >
                      <source src={URL.createObjectURL(vid)} type={vid.type} />
                      Your browser does not support video.
                    </video>
                    <div className="mt-1 flex justify-between text-xs">
                      {highlighted ? (
                        <span className="text-xs font-medium text-green-600">
                          Default Video
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setDefaultMedia(config)}
                          className="text-blue-600"
                        >
                          Set Default
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => removeNewMedia(index, "articleVideos")}
                        className="text-red-600"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                );
              })}

              {formData.existingVideos?.map((vid, index) => {
                const config = {
                  type: "existing",
                  mediaType: "video",
                  value: vid,
                };
                const highlighted = isDefault(config);

                return (
                  <div
                    key={`ex-vid-${index}`}
                    className={`relative rounded-xl border p-1 transition ${
                      highlighted
                        ? "scale-[1.02] shadow-lg ring-2 ring-brand-500"
                        : "hover:shadow-md"
                    }`}
                  >
                    <video
                      src={getImageUrl(vid)}
                      controls
                      className="h-32 w-full rounded-lg"
                    />
                    <div className="mt-1 flex justify-between text-xs">
                      {highlighted ? (
                        <span className="text-xs font-medium text-green-600">
                          Default Video
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setDefaultMedia(config)}
                          className="text-blue-600"
                        >
                          Set Default
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() =>
                          removeExistingMedia(index, "existingVideos")
                        }
                        className="text-red-600"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </FormSection>

        {/* <div className="flex justify-between">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="rounded-xl bg-red-500 px-6 py-3 text-white"
            >
              Back
            </button>
          )}
          <button
            type="submit"
            disabled={submitting}
            className="rounded-xl bg-brand-500 px-6 py-3 text-white"
          >
            {submitLabel || (initialData ? "Update Article" : "Create Article")}
          </button>
        </div> */}
      </form>
    </PageLayout>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h3 className="mb-4 text-lg font-semibold">{title}</h3>
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">{children}</div>
    </section>
  );
}

// function Input({ label, required, error, ...props }) {
//   return (
//     <div>
//       <label className="mb-1 block text-sm font-medium">
//         {label} {required && <span className="text-red-500">*</span>}
//       </label>
//       <input
//         {...props}
//         className={`w-full rounded-xl border p-3 ${
//           error ? "border-red-500" : ""
//         }`}
//       />
//       {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
//     </div>
//   );
// }

// function Textarea({ label, ...props }) {
//   return (
//     <div className="md:col-span-2">
//       <label className="mb-1 block text-sm font-medium">{label}</label>
//       <textarea
//         {...props}
//         className="min-h-[120px] w-full rounded-xl border p-3"
//       />
//     </div>
//   );
// }

// function SelectInput({ label, required, error, children, ...props }) {
//   return (
//     <div>
//       <label className="mb-1 block text-sm font-medium">
//         {label} {required && <span className="text-red-500">*</span>}
//       </label>
//       <select
//         {...props}
//         className={`w-full rounded-xl border p-3 ${
//           error ? "border-red-500" : ""
//         }`}
//       >
//         {children}
//       </select>
//       {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
//     </div>
//   );
// }
